import type {
  ApprovedDomain,
  SettingsState,
  StaffSignupCode,
  StaffProvisioning,
} from '@triedge/staff-settings-domain';

const APPROVED_DOMAIN_PATTERN =
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const EMPTY_STAFF_PROVISIONING: StaffProvisioning = {
  approvedDomains: [],
  staffSignupCodes: [],
  pendingStaffRequests: [],
};

const normalizeText = (value: unknown): string => String(value ?? '').trim();
const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const requireCrypto = (): Crypto => {
  if (!globalThis.crypto?.subtle || !globalThis.crypto?.getRandomValues) {
    throw new Error('Secure crypto runtime is unavailable.');
  }
  return globalThis.crypto;
};

const toHex = (value: ArrayBuffer | Uint8Array): string => {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  return Array.from(bytes)
    .map((entry) => entry.toString(16).padStart(2, '0'))
    .join('');
};

const randomHex = (byteLength: number, uppercase = false): string => {
  const runtimeCrypto = requireCrypto();
  const bytes = new Uint8Array(byteLength);
  runtimeCrypto.getRandomValues(bytes);
  const hex = toHex(bytes);
  return uppercase ? hex.toUpperCase() : hex;
};

const toIsoNow = (): string => new Date().toISOString();

export const normalizeApprovedDomain = (value: string): string =>
  normalizeText(value)
    .replace(/^@+/, '')
    .toLowerCase();

export const readStaffProvisioningState = (
  settingsState: Pick<SettingsState, 'organization' | 'staffProvisioning'>,
): StaffProvisioning => {
  const organizationRecord = settingsState.organization as SettingsState['organization'] & {
    ext?: Record<string, unknown>;
  };
  const organizationExt = asRecord(organizationRecord.ext);
  const fallback = asRecord(organizationExt.staffProvisioning);

  const approvedDomains = Array.isArray(settingsState.staffProvisioning?.approvedDomains)
    && settingsState.staffProvisioning.approvedDomains.length > 0
    ? settingsState.staffProvisioning.approvedDomains
    : (Array.isArray(fallback.approvedDomains) ? (fallback.approvedDomains as ApprovedDomain[]) : EMPTY_STAFF_PROVISIONING.approvedDomains);

  const staffSignupCodes = Array.isArray(settingsState.staffProvisioning?.staffSignupCodes)
    && settingsState.staffProvisioning.staffSignupCodes.length > 0
    ? settingsState.staffProvisioning.staffSignupCodes
    : (Array.isArray(fallback.staffSignupCodes) ? (fallback.staffSignupCodes as StaffSignupCode[]) : EMPTY_STAFF_PROVISIONING.staffSignupCodes);
  const pendingStaffRequests = Array.isArray(settingsState.staffProvisioning?.pendingStaffRequests)
    ? settingsState.staffProvisioning.pendingStaffRequests
    : EMPTY_STAFF_PROVISIONING.pendingStaffRequests;

  return {
    approvedDomains: [...approvedDomains],
    staffSignupCodes: [...staffSignupCodes],
    pendingStaffRequests: [...pendingStaffRequests],
  };
};

export const writeStaffProvisioningCompat = (
  settingsState: SettingsState,
  nextProvisioning: StaffProvisioning,
): SettingsState => {
  const organizationRecord = settingsState.organization as SettingsState['organization'] & {
    ext?: Record<string, unknown>;
  };
  const nextExt = {
    ...asRecord(organizationRecord.ext),
    staffProvisioning: nextProvisioning,
  };

  settingsState.organization = {
    ...organizationRecord,
    ext: nextExt,
  };

  return settingsState;
};

export const isValidApprovedDomain = (value: string): boolean =>
  APPROVED_DOMAIN_PATTERN.test(normalizeApprovedDomain(value));

export const normalizeStaffSignupCode = (value: string): string => {
  const trimmed = normalizeText(value);
  if (!trimmed) return '';
  const lastColon = trimmed.lastIndexOf(':');
  const token = lastColon >= 0 ? trimmed.slice(lastColon + 1) : trimmed;
  return token.trim().toUpperCase();
};

export const buildMaskedStaffSignupCode = (fullValue: string): string => {
  const normalized = normalizeStaffSignupCode(fullValue);
  if (!normalized) return '••••';
  const visibleSuffix = normalized.slice(-4);
  const hiddenLength = Math.max(4, normalized.length - visibleSuffix.length);
  return `${'•'.repeat(hiddenLength)}${visibleSuffix}`;
};

const buildStaffSignupCodeHash = async (code: string, salt: string): Promise<string> => {
  const runtimeCrypto = requireCrypto();
  const payload = new TextEncoder().encode(`${salt}:${normalizeStaffSignupCode(code)}`);
  const digest = await runtimeCrypto.subtle.digest('SHA-256', payload);
  return toHex(digest);
};

const resolveActorEmail = (value: string): string =>
  normalizeText(value).toLowerCase() || 'unknown@intellio.local';

export const createApprovedDomainRecord = (
  domain: string,
  actorEmail: string,
): ApprovedDomain => ({
  domain: normalizeApprovedDomain(domain),
  addedAt: toIsoNow(),
  addedBy: resolveActorEmail(actorEmail),
});

export const createStaffSignupCodeRecord = async (args: {
  label: string;
  actorEmail: string;
}): Promise<{
  record: StaffSignupCode;
  fullValue: string;
}> => {
  const fullValue = randomHex(6, true);
  const salt = randomHex(16);
  const now = toIsoNow();
  return {
    fullValue,
    record: {
      codeId: globalThis.crypto?.randomUUID?.() ?? `staff-code-${Date.now()}`,
      label: normalizeText(args.label) || 'Staff Signup Code',
      maskedValue: buildMaskedStaffSignupCode(fullValue),
      codeHash: await buildStaffSignupCodeHash(fullValue, salt),
      salt,
      status: 'active',
      createdAt: now,
      createdBy: resolveActorEmail(args.actorEmail),
      rotatedAt: null,
      rotatedBy: null,
      revokedAt: null,
      revokedBy: null,
    },
  };
};

export const rotateStaffSignupCodeRecord = async (
  record: StaffSignupCode,
  actorEmail: string,
): Promise<{
  record: StaffSignupCode;
  fullValue: string;
}> => {
  const fullValue = randomHex(6, true);
  const salt = randomHex(16);
  const rotatedAt = toIsoNow();
  return {
    fullValue,
    record: {
      ...record,
      maskedValue: buildMaskedStaffSignupCode(fullValue),
      codeHash: await buildStaffSignupCodeHash(fullValue, salt),
      salt,
      status: 'active',
      rotatedAt,
      rotatedBy: resolveActorEmail(actorEmail),
      revokedAt: null,
      revokedBy: null,
    },
  };
};

export const revokeStaffSignupCodeRecord = (
  record: StaffSignupCode,
  actorEmail: string,
): StaffSignupCode => ({
  ...record,
  status: 'revoked',
  revokedAt: toIsoNow(),
  revokedBy: resolveActorEmail(actorEmail),
});
